import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getDashboardStats,
  getAdvancedAlerts,
  getFollowUpSuggestions,
  getClinicalSummary,
  getUsDemoDashboard,
  getUsAwvClinicalSummaryPDF,
  getUsPersonalizedPreventionPlanPDF,
  getUsPatientCaregiverSummaryPDF,
  getUsDemoAwvClinicalSummaryPDF,
  getUsDemoPersonalizedPreventionPlanPDF,
  getUsDemoPatientCaregiverSummaryPDF,
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  AlertTriangle,
  Calendar,
  Clock,
  UserX,
  ArrowRight,
  ClipboardPlus,
  ShieldAlert,
  Heart,
  Apple,
  Brain,
  Pill,
  Activity,
  Bell,
  ChevronRight,
  Smile,
  Footprints,
  CheckCircle2,
  ShieldCheck,
  Percent,
  Circle,
  Stethoscope,
  FileText,
} from "lucide-react";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import { isUsClinicalHost, isUsDemoMode as detectUsDemoMode, USA_PRO_MONTHLY_PRICE_LABEL } from "../lib/usClinicalHost";
// import { useAuroraTour } from "../hooks/useAuroraTour";
// import { TOUR_CONFIG } from "../lib/auroraTourSteps";
// import { startAuroraTour, waitForTourTargets } from "../lib/auroraTour";
import WelcomeModal from "../components/WelcomeModal";
import DemoTour from "../components/DemoTour";
import DemoTooltip from "../components/DemoTooltip";
import PatientRecordLink from "../components/PatientRecordLink";
import { patientRecordLabel, resolvePatientRecordId } from "../lib/patientRecordId";

const LATAM_SHOW_APPOINTMENT_CARDS = false;

const RiskBadge = ({ level }) => {
  const styles = {
    alto: "border-sky-200 bg-sky-50 text-sky-800",
    medio: "border-slate-200 bg-slate-100 text-slate-700",
    bajo: "border-slate-200 bg-slate-50 text-slate-600",
  };
  const labels = {
    alto: "Alto Riesgo",
    medio: "Riesgo Medio",
    bajo: "Bajo Riesgo",
  };

  return (
    <Badge variant="outline" className={`${styles[level]} border`}>
      {labels[level]}
    </Badge>
  );
};

const PriorityCard = ({ title, value, subtitle, tone = "slate" }) => {
  const tones = {
    amber: "border-amber-200 bg-amber-50/60",
    red: "border-red-200 bg-red-50/60",
    sky: "border-sky-200 bg-sky-50/60",
    purple: "border-purple-200 bg-purple-50/60",
    slate: "border-slate-200 bg-slate-50/60",
  };
  return (
    <Card className={tones[tone] || tones.slate}>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-2">{value ?? 0}</p>
        <p className="text-xs text-slate-600 mt-1">{subtitle}</p>
      </CardContent>
    </Card>
  );
};

const RiskMetric = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-lg font-semibold text-slate-900">{value ?? 0}</p>
  </div>
);

function formatUsSteadiPriority(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent") return "Urgent";
  if (p === "high") return "High";
  if (p === "routine") return "Routine";
  return priority ? String(priority) : "";
}

function formatUsClinicalPriority(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent") return "Urgent";
  if (p === "high") return "High";
  if (p === "moderate") return "Moderate";
  if (p === "routine") return "Routine";
  return priority ? String(priority) : "";
}

function pickUsSteadiPrimaryAction(steadi) {
  const actions = Array.isArray(steadi?.recommended_actions) ? steadi.recommended_actions : [];
  const first = actions[0];
  return first?.text ? String(first.text) : "";
}

function pickUsMoodPrimaryAction(mood) {
  const actions = Array.isArray(mood?.recommended_actions) ? mood.recommended_actions : [];
  const first = actions[0];
  return first?.text ? String(first.text) : "";
}

function pickUsCognitionPrimaryAction(cognition) {
  const actions = Array.isArray(cognition?.recommended_actions) ? cognition.recommended_actions : [];
  const first = actions[0];
  return first?.text ? String(first.text) : "";
}

function pickUsFunctionalPrimaryAction(functional) {
  const actions = Array.isArray(functional?.recommended_actions) ? functional.recommended_actions : [];
  const first = actions[0];
  return first?.text ? String(first.text) : "";
}

function pickUsFrailtyPrimaryAction(frailty) {
  const actions = Array.isArray(frailty?.recommended_actions) ? frailty.recommended_actions : [];
  const first = actions[0];
  return first?.text ? String(first.text) : "";
}

function formatUsCognitiveStatusLabel(status) {
  const s = String(status || "").replace(/_/g, " ").trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatUsMoodStatusLabel(status) {
  const s = String(status || "").replace(/_/g, " ").trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatUsFunctionalStatusLabel(status) {
  const s = String(status || "").replace(/_/g, " ").trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatUsFrailtyStatusLabel(status) {
  const s = String(status || "").replace(/_/g, " ").trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function Dashboard() {
  const { isBillingExempt, isPremium, user } = useAuth();
  const isUsHost = isUsClinicalHost();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [followUpSuggestions, setFollowUpSuggestions] = useState(null);
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAlertTab, setActiveAlertTab] = useState("caidas");
  const isUsDemoMode = detectUsDemoMode();
  const safeAdvancedAlerts = alerts ?? {};
  const safeAlertTotals = safeAdvancedAlerts?.totals ?? {};
  const safeAlertBuckets = safeAdvancedAlerts?.alerts ?? {};
  const safeFollowUp = followUpSuggestions ?? {};
  const safeFollowUpList = Array.isArray(safeFollowUp?.suggestions) ? safeFollowUp.suggestions : [];

  const openUsPdf = async (loader, consultationId) => {
    if (!consultationId) return;
    try {
      const response = await loader(consultationId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Unable to generate report");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, alertsRes, followUpRes, summaryRes] = await Promise.all([
          isUsDemoMode ? getUsDemoDashboard() : getDashboardStats(),
          isUsDemoMode ? Promise.resolve({ data: null }) : getAdvancedAlerts(),
          isUsDemoMode ? Promise.resolve({ data: null }) : getFollowUpSuggestions(),
          isUsDemoMode ? Promise.resolve({ data: null }) : getClinicalSummary(),
        ]);
        setStats(statsRes.data);
        setAlerts(alertsRes.data);
        setFollowUpSuggestions(followUpRes.data);
        setClinicalSummary(summaryRes.data);
      } catch (error) {
        toast.error(isUsHost ? "Unable to load dashboard data" : "Error al cargar estadísticas");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isUsHost, isUsDemoMode]);

  // useAuroraTour(TOUR_CONFIG.dashboard.key, TOUR_CONFIG.dashboard.steps, {
  //   enabled: !loading && !isUsHost,
  //   delay: 900,
  // });

  const handleVerTour = async () => {
    try {
      // localStorage.removeItem(TOUR_CONFIG.dashboard.key);
    } catch {
      /* ignore */
    }
    // const selectors = TOUR_CONFIG.dashboard.steps.map((s) => s.attachTo?.element).filter(Boolean);
    // await waitForTourTargets(selectors, { timeoutMs: 12000 });
    // startAuroraTour({
    //   storageKey: TOUR_CONFIG.dashboard.key,
    //   steps: TOUR_CONFIG.dashboard.steps,
    // });
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const safeStats = stats ?? {};
  const recentVisits = Array.isArray(safeStats?.recent_visits) ? safeStats.recent_visits : [];
  const highRiskFlags = Array.isArray(safeStats?.high_risk_flags) ? safeStats.high_risk_flags : [];
  const awvDuePatients = Array.isArray(safeStats?.awv_due_patients) ? safeStats.awv_due_patients : [];
  const followUpNeeded = Array.isArray(safeStats?.follow_up_needed) ? safeStats.follow_up_needed : [];
  const smartAlerts = Array.isArray(safeStats?.simple_alerts) ? safeStats.simple_alerts : [];
  const riskCounts = {
    fallRisk: highRiskFlags.filter((i) => (i?.flags || []).includes("Fall Risk")).length,
    frailty: Number(safeAlertTotals?.fragilidad_alta ?? 0),
    polypharmacy: highRiskFlags.filter((i) => (i?.flags || []).includes("Polypharmacy")).length,
    cognitive: highRiskFlags.filter((i) => (i?.flags || []).includes("Cognitive Concern")).length,
    functionalDecline: highRiskFlags.filter((i) => (i?.flags || []).includes("Functional Decline")).length,
    mood: highRiskFlags.filter((i) => (i?.flags || []).includes("Mood Concern")).length,
  };
  const highFallRiskCount = highRiskFlags.filter((i) => String(i?.fall_risk_level || "").toLowerCase() === "high risk").length;
  const cognitiveConcernCount = riskCounts.cognitive;
  const recentPdfTargetId = recentVisits[0]?.id || null;
  const firstDemoVisitId = recentVisits[0]?.id || null;
  const firstDemoPatientId = recentVisits[0]?.patient_id || null;
  const demoPatients = Array.isArray(safeStats?.demo_patients) ? safeStats.demo_patients : [];

  if (isUsHost) {
    const clinicalPdfLoader = isUsDemoMode ? getUsDemoAwvClinicalSummaryPDF : getUsAwvClinicalSummaryPDF;
    const preventionPdfLoader = isUsDemoMode ? getUsDemoPersonalizedPreventionPlanPDF : getUsPersonalizedPreventionPlanPDF;
    const caregiverPdfLoader = isUsDemoMode ? getUsDemoPatientCaregiverSummaryPDF : getUsPatientCaregiverSummaryPDF;

    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-us-clinical-overview">
        {!isBillingExempt && !isPremium && (
          <div className="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50/60 to-white p-4 sm:p-5 shadow-sm">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto min-h-[3rem] px-8 text-base font-semibold bg-slate-800 hover:bg-slate-700 text-white shadow-md transition-shadow hover:shadow-lg"
            >
              <Link to="/checkout">{`Upgrade to Pro — ${USA_PRO_MONTHLY_PRICE_LABEL}`}</Link>
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Clinical Overview</h1>
            <p className="text-sm text-slate-600 mt-1">Age-Friendly geriatric practice for U.S. clinicians</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-slate-800 hover:bg-slate-700">
              <Link to={isUsDemoMode ? "/nueva-consulta?us_demo=1" : "/nueva-consulta"}>Start New Visit</Link>
            </Button>
          </div>
        </div>

        {isUsDemoMode && (
          <Card className="border-sky-200 bg-gradient-to-r from-sky-50/70 to-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Demo Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-700">
                Welcome to the Aurora Geriatric product demo. Follow this quick walkthrough to show the full clinical workflow.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="rounded-lg border border-slate-200 bg-white p-3"><span className="font-semibold text-slate-800">1.</span> Review a patient</div>
                <div className="rounded-lg border border-slate-200 bg-white p-3"><span className="font-semibold text-slate-800">2.</span> Open a visit</div>
                <div className="rounded-lg border border-slate-200 bg-white p-3"><span className="font-semibold text-slate-800">3.</span> Generate a clinical summary</div>
              </div>
              <Button asChild className="bg-slate-800 hover:bg-slate-700">
                <Link
                  to={
                    firstDemoVisitId && firstDemoPatientId
                      ? `/nueva-consulta/${firstDemoPatientId}?us_demo=1&demo_visit=${firstDemoVisitId}`
                      : "/pacientes?us_demo=1"
                  }
                >
                  Start Demo Walkthrough
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <PriorityCard title="AWV Due" value={awvDuePatients.length} tone="amber" subtitle="Annual wellness review pending" />
          <PriorityCard title="Follow-Up Needed" value={followUpNeeded.length} tone="sky" subtitle="Requires near-term reassessment" />
          <PriorityCard title="High Fall Risk" value={highFallRiskCount} tone="red" subtitle="High-risk mobility profile" />
          <PriorityCard title="Cognitive Concern" value={cognitiveConcernCount} tone="purple" subtitle="Mentation follow-up candidates" />
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">AWV Completion (Recent Visits)</CardTitle>
          </CardHeader>
          <CardContent>
            {!recentVisits.length ? (
              <p className="text-sm text-slate-500">No recent visits available.</p>
            ) : (
              <div className="space-y-2">
                {recentVisits.slice(0, 6).map((visit) => {
                  const compliance = visit?.awv_compliance || {};
                  const score = Number.isFinite(Number(compliance?.awv_completion_score))
                    ? Number(compliance.awv_completion_score)
                    : null;
                  const missing = Array.isArray(compliance?.missing_items) ? compliance.missing_items : [];
                  return (
                    <div key={`awv-${visit?.id}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-800">{visit?.patient_name || "Unknown patient"}</p>
                        <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">
                          {score == null ? "Not scored" : `${score}% complete`}
                        </Badge>
                      </div>
                      {missing.length ? (
                        <p className="text-xs text-amber-700 mt-1">
                          Missing: {missing.slice(0, 3).join(" | ")}{missing.length > 3 ? " | ..." : ""}
                        </p>
                      ) : (
                        <p className="text-xs text-emerald-700 mt-1">All tracked AWV documentation items completed.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Clinical Risk Panel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <RiskMetric label="Fall Risk" value={riskCounts.fallRisk} />
              <RiskMetric label="Frailty" value={riskCounts.frailty} />
              <RiskMetric label="Polypharmacy" value={riskCounts.polypharmacy} />
              <RiskMetric label="Cognitive Concern" value={riskCounts.cognitive} />
              <RiskMetric label="Functional Decline" value={riskCounts.functionalDecline} />
              <RiskMetric label="Mood Concern" value={riskCounts.mood} />
            </div>
          </CardContent>
        </Card>

        {isUsDemoMode && demoPatients.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Demo Patients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {demoPatients.map((p) => {
                  const relatedVisit = recentVisits.find((v) => v?.patient_id === p?.id);
                  const href = relatedVisit
                    ? `/nueva-consulta/${p.id}?us_demo=1&demo_visit=${relatedVisit.id}`
                    : "/pacientes?us_demo=1";
                  return (
                    <Link key={p.id} to={href} className="block p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                      <p className="font-medium text-sm text-slate-800">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.age} years</p>
                      <p className="text-xs text-slate-600 mt-1">{p.primary_case}</p>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Smart Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            {!smartAlerts.length ? (
              <p className="text-sm text-slate-500">No actionable alerts at this time.</p>
            ) : (
              <div className="space-y-2">
                {smartAlerts.slice(0, 8).map((alert) => (
                  <div key={alert} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {alert}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Recent Visits</CardTitle>
          </CardHeader>
          <CardContent>
            {!recentVisits.length ? (
              <p className="text-sm text-slate-500">No recent visits available.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3">Patient</th>
                      <th className="py-2 pr-3">Visit Type</th>
                      <th className="py-2 pr-3">Chief Concern</th>
                      <th className="py-2 pr-3">Risk Level</th>
                      <th className="py-2 pr-3">STEADI Priority</th>
                      <th className="py-2 pr-3">Next Step</th>
                      <th className="py-2 pr-3 min-w-[140px]">Cognition (screening)</th>
                      <th className="py-2 pr-3 min-w-[140px]">Mood (screening)</th>
                      <th className="py-2 pr-3 min-w-[150px]">Functional (screening)</th>
                      <th className="py-2 pr-3 min-w-[150px]">Frailty (screening)</th>
                      <th className="py-2 pr-3 min-w-[200px]">Medication Review</th>
                      <th className="py-2 pr-3 min-w-[200px]">Social Risk (SDOH)</th>
                      <th className="py-2 pr-3 min-w-[200px]">Advance Care Planning</th>
                      <th className="py-2 pr-3 min-w-[180px]">AWV Completion</th>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVisits.slice(0, 8).map((visit) => {
                      const riskLevelRaw = String(visit?.fall_risk_level || "").toLowerCase();
                      const riskTone =
                        riskLevelRaw === "high risk"
                          ? "border-red-200 bg-red-50 text-red-700"
                          : riskLevelRaw === "moderate risk"
                            ? "border-amber-200 bg-amber-50 text-amber-700"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700";
                      const riskLabel =
                        riskLevelRaw === "high risk"
                          ? "High"
                          : riskLevelRaw === "moderate risk"
                            ? "Moderate"
                            : "Low";
                      const steadi = visit?.steadi;
                      const priorityLabel = formatUsSteadiPriority(steadi?.priority);
                      const nextStep = pickUsSteadiPrimaryAction(steadi);
                      const cognition = visit?.cognition;
                      const cognitionActive = cognition?.cognitive_status && cognition.cognitive_status !== "normal";
                      const cognitionNext = pickUsCognitionPrimaryAction(cognition);
                      const mood = visit?.mood;
                      const moodActive = mood?.mood_status && mood.mood_status !== "normal";
                      const moodNext = pickUsMoodPrimaryAction(mood);
                      const functional = visit?.functional;
                      const functionalHasPlan = Boolean(functional?.functional_status);
                      const functionalNext = pickUsFunctionalPrimaryAction(functional);
                      const frailty = visit?.frailty;
                      const frailtyHasPlan = Boolean(frailty?.frailty_status);
                      const frailtyNext = pickUsFrailtyPrimaryAction(frailty);
                      const medicationReview = visit?.medication_review || {};
                      const medCount = Number(medicationReview?.medication_count || 0);
                      const medPoly = Boolean(medicationReview?.polypharmacy_detected);
                      const medPriority = formatUsClinicalPriority(medicationReview?.priority) || "Routine";
                      const medFollowUp = medicationReview?.follow_up_interval || "12 months";
                      const medAction =
                        Array.isArray(medicationReview?.recommended_actions) && medicationReview.recommended_actions.length
                          ? medicationReview.recommended_actions[0]?.text
                          : "";
                      const sdoh = visit?.sdoh_screening || {};
                      const sdohPositiveDomains = Array.isArray(sdoh?.positive_domains) ? sdoh.positive_domains : [];
                      const sdohPriority = formatUsClinicalPriority(sdoh?.priority) || "Routine";
                      const sdohFollowUp = sdoh?.follow_up_interval || "12 months";
                      const acp = visit?.advance_care_planning || {};
                      const acpPriority = formatUsClinicalPriority(acp?.priority) || "Routine";
                      const acpFollowUp = acp?.follow_up_interval || "next preventive visit";
                      const acpAction =
                        Array.isArray(acp?.recommended_actions) && acp.recommended_actions.length
                          ? acp.recommended_actions[0]?.text
                          : "";
                      const awvCompliance = visit?.awv_compliance || {};
                      const awvScore = Number.isFinite(Number(awvCompliance?.awv_completion_score))
                        ? Number(awvCompliance.awv_completion_score)
                        : null;
                      const awvMissing = Array.isArray(awvCompliance?.missing_items) ? awvCompliance.missing_items : [];
                      return (
                        <tr key={visit?.id} className="border-b last:border-0">
                          <td className="py-2 pr-3 font-medium text-slate-800">{visit?.patient_name || "Unknown patient"}</td>
                          <td className="py-2 pr-3 text-slate-600">{visit?.visit_type || "Visit"}</td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[280px] truncate">{visit?.chief_concern || "No chief concern recorded"}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className={riskTone}>
                              {riskLabel}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-slate-600">
                            {priorityLabel ? <span className="text-xs font-medium text-slate-700">{priorityLabel}</span> : "—"}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[260px]">
                            {nextStep ? <span className="line-clamp-3 text-xs">{nextStep}</span> : "—"}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[200px] align-top">
                            {cognitionActive ? (
                              <div className="space-y-1 text-[11px] text-slate-600">
                                <div>
                                  <span className="font-semibold text-slate-700">Status:</span>{" "}
                                  {formatUsCognitiveStatusLabel(cognition?.cognitive_status)}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                  {formatUsClinicalPriority(cognition?.priority) || "—"}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                  {cognition?.follow_up_interval || "—"}
                                </div>
                                {cognitionNext ? (
                                  <div className="line-clamp-3">
                                    <span className="font-semibold text-slate-700">Next step:</span> {cognitionNext}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">Routine cognitive screen</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[200px] align-top">
                            {moodActive ? (
                              <div className="space-y-1 text-[11px] text-slate-600">
                                <div>
                                  <span className="font-semibold text-slate-700">Status:</span> {formatUsMoodStatusLabel(mood?.mood_status)}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                  {formatUsClinicalPriority(mood?.priority) || "—"}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                  {mood?.follow_up_interval || "—"}
                                </div>
                                {moodNext ? (
                                  <div className="line-clamp-3">
                                    <span className="font-semibold text-slate-700">Next step:</span> {moodNext}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">Routine mood screen</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[200px] align-top">
                            {functionalHasPlan ? (
                              <div className="space-y-1 text-[11px] text-slate-600">
                                <div>
                                  <span className="font-semibold text-slate-700">Status:</span>{" "}
                                  {formatUsFunctionalStatusLabel(functional?.functional_status)}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                  {formatUsClinicalPriority(functional?.priority) || "—"}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                  {functional?.follow_up_interval || "—"}
                                </div>
                                {functionalNext ? (
                                  <div className="line-clamp-3">
                                    <span className="font-semibold text-slate-700">Next step:</span> {functionalNext}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[200px] align-top">
                            {frailtyHasPlan ? (
                              <div className="space-y-1 text-[11px] text-slate-600">
                                <div>
                                  <span className="font-semibold text-slate-700">Status:</span>{" "}
                                  {formatUsFrailtyStatusLabel(frailty?.frailty_status)}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                  {formatUsClinicalPriority(frailty?.priority) || "—"}
                                </div>
                                <div>
                                  <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                  {frailty?.follow_up_interval || "—"}
                                </div>
                                {frailtyNext ? (
                                  <div className="line-clamp-3">
                                    <span className="font-semibold text-slate-700">Next step:</span> {frailtyNext}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[240px] align-top">
                            <div className="space-y-1 text-[11px]">
                              <div>
                                <span className="font-semibold text-slate-700">Count:</span> {medCount}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Status:</span> {medPoly ? "Polypharmacy flagged" : "No polypharmacy flag"}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Priority:</span> {medPriority}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Follow-up:</span> {medFollowUp}
                              </div>
                              {medAction ? (
                                <div className="line-clamp-2">
                                  <span className="font-semibold text-slate-700">Next step:</span> {medAction}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[240px] align-top">
                            <div className="space-y-1 text-[11px]">
                              <div>
                                <span className="font-semibold text-slate-700">Status:</span>{" "}
                                {String(sdoh?.sdoh_status || "no_positive_screen").replace(/_/g, " ")}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Positive domains:</span>{" "}
                                {sdohPositiveDomains.length ? sdohPositiveDomains.length : 0}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Priority:</span> {sdohPriority}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Follow-up:</span> {sdohFollowUp}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[240px] align-top">
                            <div className="space-y-1 text-[11px]">
                              <div>
                                <span className="font-semibold text-slate-700">Status:</span>{" "}
                                {String(acp?.acp_status || "pending_documentation").replace(/_/g, " ")}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Priority:</span> {acpPriority}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Follow-up:</span> {acpFollowUp}
                              </div>
                              {acpAction ? (
                                <div className="line-clamp-2">
                                  <span className="font-semibold text-slate-700">Next step:</span> {acpAction}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-600 max-w-[220px] align-top">
                            <div className="space-y-1 text-[11px]">
                              <div>
                                <span className="font-semibold text-slate-700">Completion:</span>{" "}
                                {awvScore == null ? "Not scored" : `${awvScore}%`}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700">Missing:</span>{" "}
                                {awvMissing.length ? awvMissing.slice(0, 2).join(" | ") : "None"}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-slate-600">{visit?.created_at ? new Date(visit.created_at).toLocaleDateString("en-US") : "—"}</td>
                          <td className="py-2 pr-3">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openUsPdf(clinicalPdfLoader, visit?.id)}>
                              Open
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button asChild variant="outline"><Link to={isUsDemoMode ? "/nueva-consulta?us_demo=1" : "/nueva-consulta"}>Start AWV</Link></Button>
                <Button asChild variant="outline"><Link to={isUsDemoMode ? "/nueva-consulta?us_demo=1" : "/nueva-consulta"}>Start Fall Risk Visit</Link></Button>
                <Button asChild variant="outline"><Link to={isUsDemoMode ? "/nueva-consulta?us_demo=1" : "/nueva-consulta"}>Start Cognitive Follow-Up</Link></Button>
                <Button asChild variant="outline"><Link to="/dashboard?us_demo=1">Open Demo</Link></Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
          <CardTitle className="text-lg">PDF Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button variant="outline" disabled={!recentPdfTargetId} onClick={() => openUsPdf(clinicalPdfLoader, recentPdfTargetId)}>Clinical Summary</Button>
                <Button variant="outline" disabled={!recentPdfTargetId} onClick={() => openUsPdf(preventionPdfLoader, recentPdfTargetId)}>Prevention Care Plan</Button>
                <Button variant="outline" disabled={!recentPdfTargetId} onClick={() => openUsPdf(caregiverPdfLoader, recentPdfTargetId)}>Patient & Caregiver Summary</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const latamDoctorDisplayName = (() => {
    const raw = String(user?.nombre || "").trim();
    if (!raw) return "Usuario";
    return raw.replace(/^Dr\.?\s+/i, "").trim() || raw;
  })();
  const latamHour = new Date().getHours();
  const latamSaludo =
    latamHour >= 6 && latamHour < 12
      ? "Buenos días"
      : latamHour >= 12 && latamHour < 20
        ? "Buenas tardes"
        : "Buenas noches";

  const latamDatetimeLine = (() => {
    const tz = "America/Guatemala";
    try {
      const now = new Date();
      const dateStr = new Intl.DateTimeFormat("es-GT", {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: tz,
      }).format(now);
      const timeStr = new Intl.DateTimeFormat("es-GT", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      }).format(now);
      const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
      return `${cap(dateStr)} · ${timeStr} · Guatemala`;
    } catch {
      return "";
    }
  })();

  const latamTotalPatientsDisplay = clinicalSummary?.total_pacientes ?? stats?.total_patients ?? 0;
  const latamShowWelcomeBanner = latamTotalPatientsDisplay === 0;

  return (
    <div
      className="relative mx-auto w-full min-w-0 max-w-[1400px] space-y-8 rounded-2xl px-2 pb-4 pt-1 ring-1 ring-slate-200/60 animate-fade-in sm:px-3 lg:px-4"
      data-testid="dashboard"
    >
      <WelcomeModal />
      <DemoTour />
      {!isBillingExempt && !isPremium && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <Button
            asChild
            size="lg"
            className="w-full min-h-[3rem] bg-slate-800 px-8 text-base font-semibold text-white shadow-sm transition-shadow hover:bg-slate-700 hover:shadow-md sm:w-auto"
          >
            <Link to="/checkout">Actualizar a Premium - $39/mes</Link>
          </Button>
        </div>
      )}

      <div className="hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-sky-300 text-sky-700 hover:bg-sky-50"
          data-testid="dashboard-ver-tour"
          onClick={handleVerTour}
        >
          🌅 Ver guía de Aurora
        </Button>
      </div>

      {latamShowWelcomeBanner && (
        <div
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="latam-welcome-banner"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div className="flex min-w-0 gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50 text-sky-700 shadow-sm">
                <Stethoscope className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 space-y-2">
                <p className="text-base font-semibold tracking-tight text-slate-900 sm:text-lg">Bienvenido a Aurora Geriátrica</p>
                <p className="text-sm leading-relaxed text-slate-600">
                  Empieza aquí: crea tu primer paciente y registra una consulta geriátrica estructurada.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                asChild
                className="rounded-2xl bg-sky-600 px-5 text-white shadow-sm hover:bg-sky-700"
              >
                <Link to="/pacientes" data-aurora-tour="dashboard-new-patient">Crear primer paciente</Link>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header — saludo clínico (LATAM); mismo destino /nueva-consulta */}
      <div
        data-aurora-tour="dashboard-main"
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="flex min-w-0 flex-col gap-6 border-b border-slate-100 pb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Logo
              variant="wordmark"
              format="png"
              className="h-7 max-w-[min(72vw,220px)] shrink-0 object-contain object-left sm:h-8"
              alt={BRAND_NAME}
            />
            <div className="hidden h-14 w-px shrink-0 bg-slate-200 sm:block" aria-hidden />
            <div className="min-w-0 space-y-1.5">
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                Consulta geriátrica digital completa
              </span>
              <div className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-sky-700" />
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Consulta Geriátrica Digital</h1>
              </div>
              <p className="text-base font-medium text-slate-800">{latamSaludo}, Dr. {latamDoctorDisplayName} 👋</p>
              {latamDatetimeLine ? (
                <p className="text-sm text-slate-500">{latamDatetimeLine}</p>
              ) : null}
              <p className="text-sm text-slate-500">Historia clínica, seguimiento longitudinal e informes PDF en un solo lugar.</p>
              <p className="text-xs leading-relaxed text-slate-500">
                Inicia una evaluación completa o revisa el estado de tus pacientes.
              </p>
            </div>
          </div>
          <div className="relative inline-block shrink-0 sm:self-center">
            <DemoTooltip message="Inicia aquí el flujo completo: historia clínica → examen físico → plan terapéutico → informe clínico personalizable." position="top" />
            <div className="flex flex-col items-stretch gap-2 sm:items-end">
              <Link to="/nueva-consulta" className="shrink-0 sm:self-center">
                <Button
                  className="h-11 rounded-2xl bg-sky-600 px-6 py-2.5 text-white shadow-sm transition-all duration-200 hover:bg-sky-700 hover:shadow-md"
                  data-testid="new-consultation-btn"
                >
                  <ClipboardPlus className="mr-2 h-4 w-4" />
                  + Nueva evaluación
                </Button>
              </Link>
              <Link
                to="/panel/visual-v1"
                className="text-center text-xs text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 rounded-sm"
                data-testid="try-visual-cover"
              >
                Probar nueva carátula
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-8 ${latamTotalPatientsDisplay === 0 ? "lg:grid-cols-2" : ""}`}>
        {latamTotalPatientsDisplay === 0 && (
          <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
            <CardHeader className="pb-2 pt-6">
              <CardTitle className="text-base font-semibold tracking-tight text-slate-900">Primeros pasos clínicos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pb-6 pt-0">
              <div className="flex gap-3">
                <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" aria-hidden />
                <div>
                  <p className="text-sm font-medium text-slate-900">Crear paciente</p>
                  <p className="text-xs text-slate-500">Registra a quien vas a seguir en consulta.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" aria-hidden />
                <div>
                  <p className="text-sm font-medium text-slate-900">Realizar evaluación geriátrica</p>
                  <p className="text-xs text-slate-500">Completa una consulta geriátrica y registra el seguimiento.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" aria-hidden />
                <div>
                  <p className="text-sm font-medium text-slate-900">Generar informe clínico</p>
                  <p className="text-xs text-slate-500">Revisa historial e informes desde el módulo de reportes.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardHeader className="pb-2 pt-6">
            <CardTitle className="text-base font-semibold tracking-tight text-slate-900">Accesos rápidos</CardTitle>
          </CardHeader>
          <CardContent className="pb-6 pt-0">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Link
                to="/nueva-consulta"
                className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-800 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50 text-sky-700">
                  <ClipboardPlus className="h-6 w-6" />
                </span>
                <span className="min-w-0 truncate">Nueva evaluación</span>
              </Link>
              <Link
                to="/pacientes"
                data-aurora-tour="dashboard-new-patient"
                className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-800 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50 text-sky-700">
                  <Users className="h-6 w-6" />
                </span>
                <span className="min-w-0 truncate">Pacientes</span>
              </Link>
              <Link
                to="/reportes"
                className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-800 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50 text-sky-700">
                  <FileText className="h-6 w-6" />
                </span>
                <span className="min-w-0 truncate">Historial clínico</span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {isUsDemoMode && (
        <Card className="border-sky-200 bg-sky-50/60">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-sky-900">USA Demo Mode Active</p>
              <p className="text-xs text-sky-700">Showing fictional geriatric patients and visits for product demonstration.</p>
            </div>
            <Badge className="bg-sky-100 text-sky-800 border-sky-200">Demo Dataset</Badge>
          </CardContent>
        </Card>
      )}

      {/* Tarjetas clínicas (clinical-summary) — mismos datos, copy y skin refinados */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold tracking-tight text-slate-900">Pacientes en seguimiento</p>
                <p className="mt-1 text-xs text-slate-500">Activos en control clínico continuo</p>
                <span className="mt-3 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">Estable</span>
                <p className="mt-4 text-3xl font-semibold text-slate-900">
                  {clinicalSummary?.total_pacientes ?? stats?.total_patients ?? 0}
                </p>
              </div>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50">
                <Users className="h-7 w-7 text-sky-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold tracking-tight text-slate-900">Pacientes con riesgo activo</p>
                <p className="mt-1 text-xs text-slate-500">Requieren atención prioritaria</p>
                <span className="mt-3 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">Riesgo</span>
                <p className="mt-4 text-3xl font-semibold text-slate-900">
                  {clinicalSummary?.pacientes_en_riesgo ?? stats?.high_risk_patients?.length ?? 0}
                </p>
                <p className="mt-1 text-sm text-slate-500">({clinicalSummary?.porcentaje_en_riesgo ?? 0}%)</p>
              </div>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50">
                <AlertTriangle className="h-7 w-7 text-sky-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold tracking-tight text-slate-900">Pacientes estables</p>
                <p className="mt-1 text-xs text-slate-500">Sin alertas geriátricas actuales</p>
                <span className="mt-3 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">Estable</span>
                <p className="mt-4 text-3xl font-semibold text-slate-900">
                  {clinicalSummary?.pacientes_estables ?? 0}
                </p>
              </div>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50">
                <CheckCircle2 className="h-7 w-7 text-sky-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold tracking-tight text-slate-900">Índice de riesgo global</p>
                <p className="mt-1 text-xs text-slate-500">Del total de pacientes en seguimiento</p>
                <span className="mt-3 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">Índice</span>
                <p className="mt-4 text-3xl font-semibold text-slate-900">
                  {clinicalSummary?.porcentaje_en_riesgo ?? 0}%
                </p>
              </div>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-sky-50">
                <Percent className="h-7 w-7 text-sky-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actividad reciente — bloque visual estático (sin datos adicionales) */}
      <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-2 pt-6">
          <CardTitle className="text-base font-semibold tracking-tight text-slate-900">Actividad reciente</CardTitle>
        </CardHeader>
        <CardContent className="pb-6 pt-0">
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No se han registrado evaluaciones hoy</p>
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-slate-500">
              Cuando realices una evaluación, aquí verás la actividad reciente.
            </p>
          </div>
        </CardContent>
      </Card>

      {isUsHost && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Recent Visits</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.recent_visits?.length ? (
                <p className="text-slate-500 text-sm">No recent visits yet.</p>
              ) : (
                <div className="space-y-3">
                  {stats.recent_visits.map((visit) => (
                    <div key={visit.id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <p className="font-medium text-slate-800 text-sm">{visit.patient_name}</p>
                      <p className="text-xs text-slate-500">{visit.visit_type}</p>
                      {visit.chief_concern && (
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{visit.chief_concern}</p>
                      )}
                      {visit.steadi && (
                        <div className="mt-2 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            {visit.steadi.priority && (
                              <Badge variant="outline" className="text-[10px] border-slate-200 bg-white text-slate-700">
                                STEADI priority: {formatUsSteadiPriority(visit.steadi.priority)}
                              </Badge>
                            )}
                            {visit.steadi.follow_up_interval && (
                              <span className="text-[11px] text-slate-500">
                                Follow-up: {visit.steadi.follow_up_interval}
                              </span>
                            )}
                          </div>
                          {pickUsSteadiPrimaryAction(visit.steadi) && (
                            <p className="text-[11px] text-slate-600 line-clamp-3">
                              <span className="font-semibold text-slate-700">Next step:</span>{" "}
                              {pickUsSteadiPrimaryAction(visit.steadi)}
                            </p>
                          )}
                        </div>
                      )}
                      {visit.cognition && visit.cognition.cognitive_status && visit.cognition.cognitive_status !== "normal" && (
                        <div className="mt-2 rounded-md border border-purple-100 bg-purple-50/80 px-2 py-1.5 space-y-0.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-900">Cognition (screening)</p>
                          <p className="text-[11px] text-slate-700">
                            <span className="font-semibold">Status:</span> {formatUsCognitiveStatusLabel(visit.cognition.cognitive_status)} ·{" "}
                            <span className="font-semibold">Priority:</span> {formatUsClinicalPriority(visit.cognition.priority)} ·{" "}
                            <span className="font-semibold">Follow-up:</span> {visit.cognition.follow_up_interval || "—"}
                          </p>
                          {pickUsCognitionPrimaryAction(visit.cognition) && (
                            <p className="text-[11px] text-slate-600 line-clamp-3">
                              <span className="font-semibold text-slate-800">Next step:</span> {pickUsCognitionPrimaryAction(visit.cognition)}
                            </p>
                          )}
                        </div>
                      )}
                      {visit.mood && visit.mood.mood_status && visit.mood.mood_status !== "normal" && (
                        <div className="mt-2 rounded-md border border-sky-100 bg-sky-50/80 px-2 py-1.5 space-y-0.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-800">Mood (screening)</p>
                          <p className="text-[11px] text-slate-700">
                            <span className="font-semibold">Priority:</span> {formatUsClinicalPriority(visit.mood.priority)} ·{" "}
                            <span className="font-semibold">Follow-up:</span> {visit.mood.follow_up_interval || "—"}
                          </p>
                          {pickUsMoodPrimaryAction(visit.mood) && (
                            <p className="text-[11px] text-slate-600 line-clamp-3">
                              <span className="font-semibold text-slate-800">Next step:</span> {pickUsMoodPrimaryAction(visit.mood)}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => openUsPdf(isUsDemoMode ? getUsDemoAwvClinicalSummaryPDF : getUsAwvClinicalSummaryPDF, visit.id)}
                        >
                          View Clinical Summary
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-slate-800 hover:bg-slate-700"
                          onClick={() => openUsPdf(isUsDemoMode ? getUsDemoPersonalizedPreventionPlanPDF : getUsPersonalizedPreventionPlanPDF, visit.id)}
                        >
                          Open Care Plan
                        </Button>
                        {isUsDemoMode && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openUsPdf(getUsDemoPatientCaregiverSummaryPDF, visit.id)}
                          >
                            Patient Summary
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">High-Risk Flags</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.high_risk_flags?.length ? (
                <p className="text-slate-500 text-sm">No high-risk flags.</p>
              ) : (
                <div className="space-y-3">
                  {stats.high_risk_flags.map((flag) => {
                    const riskLevelRaw = String(flag?.fall_risk_level || "").toLowerCase();
                    const riskTone =
                      riskLevelRaw === "high risk"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : riskLevelRaw === "moderate risk"
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700";
                    const riskLabel =
                      riskLevelRaw === "high risk"
                        ? "High"
                        : riskLevelRaw === "moderate risk"
                          ? "Moderate"
                          : "Low";
                    return (
                      <div key={flag.patient_id} className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-800 text-sm">{flag.patient_name}</p>
                          {flag.fall_risk_level && (
                            <Badge variant="outline" className={`text-[10px] ${riskTone}`}>
                              {riskLabel}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-amber-700">{(flag.flags || []).join(" • ")}</p>
                        {flag.steadi && (
                          <div className="mt-2 space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              {flag.steadi.priority && (
                                <Badge variant="outline" className="text-[10px] border-amber-300 bg-white text-amber-900">
                                  STEADI: {formatUsSteadiPriority(flag.steadi.priority)}
                                </Badge>
                              )}
                              {flag.steadi.steadi_stage && (
                                <span className="text-[11px] text-amber-900/80 capitalize">
                                  Stage: {String(flag.steadi.steadi_stage).replace(/_/g, " ")}
                                </span>
                              )}
                            </div>
                            {pickUsSteadiPrimaryAction(flag.steadi) && (
                              <p className="text-[11px] text-amber-900/90 line-clamp-3">
                                <span className="font-semibold">Focus:</span> {pickUsSteadiPrimaryAction(flag.steadi)}
                              </p>
                            )}
                          </div>
                        )}
                        {flag.cognition && flag.cognition.cognitive_status && flag.cognition.cognitive_status !== "normal" && (
                          <div className="mt-2 space-y-0.5 rounded-md border border-purple-100 bg-white/80 px-2 py-1.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-900">Cognition (screening)</p>
                            <p className="text-[11px] text-amber-950/90">
                              <span className="font-semibold">Priority:</span> {formatUsClinicalPriority(flag.cognition.priority)} ·{" "}
                              <span className="font-semibold">Suggested follow-up:</span> {flag.cognition.follow_up_interval || "—"}
                            </p>
                            {pickUsCognitionPrimaryAction(flag.cognition) && (
                              <p className="text-[11px] text-amber-950/85 line-clamp-3">
                                <span className="font-semibold">Next step:</span> {pickUsCognitionPrimaryAction(flag.cognition)}
                              </p>
                            )}
                          </div>
                        )}
                        {flag.mood && flag.mood.mood_status && flag.mood.mood_status !== "normal" && (
                          <div className="mt-2 space-y-0.5 rounded-md border border-sky-100 bg-white/80 px-2 py-1.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-900">Mood (screening)</p>
                            <p className="text-[11px] text-amber-950/90">
                              <span className="font-semibold">Priority:</span> {formatUsClinicalPriority(flag.mood.priority)} ·{" "}
                              <span className="font-semibold">Suggested follow-up:</span> {flag.mood.follow_up_interval || "—"}
                            </p>
                            {pickUsMoodPrimaryAction(flag.mood) && (
                              <p className="text-[11px] text-amber-950/85 line-clamp-3">
                                <span className="font-semibold">Next step:</span> {pickUsMoodPrimaryAction(flag.mood)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.simple_alerts?.length ? (
                <p className="text-slate-500 text-sm">No alerts.</p>
              ) : (
                <div className="space-y-2">
                  {stats.simple_alerts.map((alert) => (
                    <div key={alert} className="text-sm text-slate-700 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      {alert}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isUsHost && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">AWV Due Patients</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.awv_due_patients?.length ? (
                <p className="text-slate-500 text-sm">No AWV due patients.</p>
              ) : (
                <div className="space-y-2">
                  {stats.awv_due_patients.map((item) => (
                    <div key={item.patient_id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <p className="font-medium text-sm text-slate-800">{item.patient_name}</p>
                      <p className="text-xs text-slate-600">{item.reason || "AWV Due"}</p>
                      {item?.awv_type && <p className="text-[11px] text-slate-500 mt-0.5">{item.awv_type}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">High-Risk Patients</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.high_risk_patients?.length ? (
                <p className="text-slate-500 text-sm">No high-risk patients.</p>
              ) : (
                <div className="space-y-2">
                  {stats.high_risk_patients.map((item) => (
                    <div key={item.patient_id} className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                      <p className="font-medium text-sm text-slate-800">{item.patient_name}</p>
                      <p className="text-xs text-amber-700">{(item.flags || []).join(" • ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Follow-up Needed</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.follow_up_needed?.length ? (
                <p className="text-slate-500 text-sm">No pending follow-up items.</p>
              ) : (
                <div className="space-y-2">
                  {stats.follow_up_needed.map((item, idx) => (
                    <div key={`${item.patient_id}-${idx}`} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <p className="font-medium text-sm text-slate-800">{item.patient_name}</p>
                      <p className="text-xs text-slate-600">{item.reason || "Follow-up needed"}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isUsDemoMode && stats?.demo_patients?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Demo Patients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.demo_patients.map((p) => (
                <div key={p.id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="font-medium text-sm text-slate-800">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.age} years</p>
                  <p className="text-xs text-slate-600 mt-1">{p.primary_case}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alertas clínicas — agrupadas por nivel de riesgo */}
      <Card
        className="rounded-2xl border border-amber-200/80 bg-amber-50/50 shadow-sm transition-all duration-200 hover:shadow-md"
        data-testid="clinical-alerts"
      >
        <CardHeader className="pb-3">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
              <ShieldAlert className="h-5 w-5 shrink-0 text-sky-700" />
              <span>Alertas geriátricas activas</span>
              {clinicalSummary?.lista_alertas?.length > 0 && (
                <Badge className="border-amber-200 bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {clinicalSummary.lista_alertas.length} paciente{clinicalSummary.lista_alertas.length !== 1 ? "s" : ""}
                </Badge>
              )}
            </CardTitle>
            <Link to="/pacientes">
              <Button variant="ghost" size="sm" className="text-slate-600">
                Ver pacientes
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {!clinicalSummary?.lista_alertas?.length ? (
              <div className="flex flex-col items-center justify-center px-6 py-12 text-center sm:py-14">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white">
                  <ShieldCheck className="h-8 w-8 text-sky-700" strokeWidth={1.75} />
                </div>
                <p className="text-base font-semibold tracking-tight text-slate-900">No hay alertas geriátricas activas en este momento</p>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600">
                  Cuando evalúes a un paciente, Aurora mostrará aquí riesgos como caídas, fragilidad, nutrición o polifarmacia.
                </p>
                <div className="mt-6 flex w-full max-w-md flex-col gap-2 sm:flex-row sm:justify-center">
                  <Button asChild className="rounded-2xl bg-sky-600 text-white hover:bg-sky-700">
                    <Link to="/nueva-consulta">Nueva evaluación</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-2xl border-slate-200 bg-white">
                    <Link to="/pacientes">Ver pacientes</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const lista = clinicalSummary.lista_alertas || [];
                  const altoRiesgo = lista.filter((i) => (i.riesgo_score || 0) >= 3);
                  const riesgoModerado = lista.filter((i) => (i.riesgo_score || 0) >= 1 && (i.riesgo_score || 0) < 3);
                  const ALERT_ICONS = {
                    "Deterioro cognitivo": "🧠",
                    "Alto riesgo de caídas": "🦴",
                    "Riesgo de desnutrición": "⚖️",
                    "Pérdida de peso reciente": "⚖️",
                    "Polifarmacia": "💊",
                    "Hipertensión no controlada": "❤️",
                    "Riesgo de depresión": "💭",
                    "Paciente frágil": "🩺",
                  };
                  const AlertPatientRow = ({ item }) => {
                    const recordId = resolvePatientRecordId(item);
                    return (
                    <div
                      key={recordId || item.nombre}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-amber-200/70 bg-white/90 p-4 shadow-sm transition-colors hover:border-amber-300/80 hover:shadow-md"
                      data-testid={`alert-patient-${recordId || "unlinked"}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-800">{item.nombre}</p>
                        <p className="mt-2 flex flex-wrap gap-1.5 text-sm text-amber-900/90">
                          {(item.alertas || []).map((a) => (
                            <Badge
                              key={a}
                              variant="outline"
                              className="border-amber-200 bg-white/90 px-2 py-0.5 text-xs font-medium text-amber-800"
                            >
                              <span className="mr-1">{ALERT_ICONS[a] || "•"}</span>
                              {a}
                            </Badge>
                          ))}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {recordId ? (
                          <>
                            <Link to={`/pacientes/${recordId}`}>
                              <Button variant="outline" size="sm" className="border-slate-300 text-slate-700">
                                Ver paciente
                              </Button>
                            </Link>
                            <Link to={`/nueva-consulta/${recordId}`}>
                              <Button size="sm" className="border-0 bg-sky-600 text-white hover:bg-sky-700">
                                Nueva consulta
                              </Button>
                            </Link>
                          </>
                        ) : (
                          <span className="text-xs text-slate-500">Sin identificador</span>
                        )}
                      </div>
                    </div>
                    );
                  };
                  return (
                    <>
                      {altoRiesgo.length > 0 && (
                        <div>
                          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold tracking-tight text-slate-600">
                            <span className="text-base" aria-hidden>🔴</span> Alto riesgo ({altoRiesgo.length})
                          </h3>
                          <div className="space-y-2">
                            {altoRiesgo.map((item) => (
                              <AlertPatientRow key={item.patient_id} item={item} />
                            ))}
                          </div>
                        </div>
                      )}
                      {riesgoModerado.length > 0 && (
                        <div>
                          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold tracking-tight text-slate-600">
                            <span className="text-base" aria-hidden>🟡</span> Riesgo moderado ({riesgoModerado.length})
                          </h3>
                          <div className="space-y-2">
                            {riesgoModerado.map((item) => (
                              <AlertPatientRow key={item.patient_id} item={item} />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Pacientes sin control reciente */}
      <Card
        className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md"
        data-testid="follow-up-patients"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
              <UserX className="h-5 w-5 shrink-0 text-slate-600" />
              <span>Pacientes sin control reciente</span>
              {clinicalSummary?.pacientes_sin_control > 0 && (
                <Badge className="border-amber-200 bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {clinicalSummary.pacientes_sin_control}
                </Badge>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {!clinicalSummary?.pacientes_sin_control_lista?.length ? (
              <p className="text-slate-500 text-center py-8">
                Todos los pacientes tienen consultas recientes
              </p>
            ) : (
              <div className="space-y-3">
                {clinicalSummary.pacientes_sin_control_lista.map((item) => {
                  const recordId = resolvePatientRecordId(item);
                  return (
                  <div
                    key={recordId || item.nombre}
                    className="flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-slate-100 border-l-2 border-l-slate-300 bg-slate-50 p-4 transition-colors hover:border-slate-200"
                    data-testid={`no-visit-${recordId || "unlinked"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800">{item.nombre}</p>
                      <p className="text-sm text-slate-500">{item.motivo}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {recordId ? (
                        <>
                          <Link to={`/pacientes/${recordId}`}>
                            <Button variant="outline" size="sm" className="border-slate-300 text-slate-700">
                              Ver paciente
                            </Button>
                          </Link>
                          <Link to={`/nueva-consulta/${recordId}`}>
                            <Button size="sm" className="border-0 bg-sky-600 text-white hover:bg-sky-700">
                              Nueva consulta
                            </Button>
                          </Link>
                        </>
                      ) : (
                        <span className="text-xs text-slate-500">Sin identificador</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Intelligent Follow-up Suggestions - Phase 6 Aurora Geriátrica v1.1 */}
      {safeFollowUp && (safeFollowUp?.total ?? 0) > 0 && (
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md" data-testid="follow-up-suggestions">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
                <Bell className="h-5 w-5 shrink-0 text-sky-700" />
                <span>Sugerencias de Seguimiento Inteligente</span>
                <Badge className="ml-0 border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800 sm:ml-2">
                  {safeFollowUp?.total ?? 0} pendiente{(safeFollowUp?.total ?? 0) !== 1 ? 's' : ''}
                </Badge>
                {(safeFollowUp?.high_priority ?? 0) > 0 && (
                  <Badge className="border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800">
                    {safeFollowUp?.high_priority ?? 0} urgente{(safeFollowUp?.high_priority ?? 0) !== 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {safeFollowUpList.slice(0, 6).map((suggestion, idx) => {
                const typeConfig = {
                  cognitive: { icon: Brain, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                  nutritional: { icon: Apple, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                  falls: { icon: Footprints, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                  frailty: { icon: Heart, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                  functional: { icon: Activity, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                  emotional: { icon: Smile, color: "text-sky-700", bgColor: "bg-white", borderColor: "border-slate-200" },
                };
                const config = typeConfig[suggestion.type] || typeConfig.functional;
                const Icon = config.icon;

                const priorityBadge = {
                  high: { className: "border-sky-200 bg-sky-50 text-sky-800", label: "Urgente" },
                  medium: { className: "border-slate-200 bg-slate-100 text-slate-700", label: "Recomendado" },
                  low: { className: "border-slate-200 bg-slate-50 text-slate-600", label: "Monitoreo" },
                };
                const priority = priorityBadge[suggestion.priority] || priorityBadge.medium;

                const recordId = resolvePatientRecordId(suggestion);
                const body = (
                    <div className="flex items-start gap-3">
                      <div className={`rounded-2xl border border-sky-100 bg-sky-50 p-2 ${config.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-slate-800">
                            {suggestion.patient_name}
                          </span>
                          <Badge variant="outline" className={`text-[10px] ${priority.className}`}>
                            {priority.label}
                          </Badge>
                        </div>
                        <p className="mb-1 text-xs font-medium text-slate-700">
                          {suggestion.title}
                        </p>
                        <p className="mb-2 text-xs text-slate-600">
                          {suggestion.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="rounded bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500">
                            📅 {suggestion.follow_up_period}
                          </span>
                          <ChevronRight className="h-4 w-4 text-slate-400 transition-colors group-hover:text-sky-700" />
                        </div>
                      </div>
                    </div>
                );
                if (!recordId) {
                  return (
                    <div
                      key={`follow-up-unlinked-${idx}`}
                      className={`group min-w-0 rounded-2xl border p-4 shadow-sm ${config.bgColor} ${config.borderColor}`}
                    >
                      {body}
                    </div>
                  );
                }
                return (
                  <Link
                    key={`${recordId}-${suggestion.type}-${idx}`}
                    to={`/pacientes/${recordId}`}
                    className={`group min-w-0 rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${config.bgColor} ${config.borderColor}`}
                    data-testid={`follow-up-${recordId}`}
                  >
                    {body}
                  </Link>
                );
              })}
            </div>
            {(safeFollowUp?.total ?? 0) > 6 && (
              <p className="text-xs text-center text-slate-500 mt-4 pt-3 border-t border-slate-100">
                +{(safeFollowUp?.total ?? 0) - 6} sugerencias adicionales
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {LATAM_SHOW_APPOINTMENT_CARDS && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-slate-600" />
                  Consultas de Hoy
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {stats?.today_appointments?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No hay citas programadas para hoy</p>
                ) : (
                  <div className="space-y-3">
                    {stats?.today_appointments?.map((apt) => (
                      <div
                        key={apt.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        data-testid={`appointment-${apt.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{apt.paciente_nombre}</p>
                            <p className="text-sm text-slate-500">{apt.motivo}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-white">
                          {apt.hora}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* High Risk Patients */}
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
                <AlertTriangle className="h-5 w-5 shrink-0 text-sky-700" />
                Pacientes de Alto Riesgo
              </CardTitle>
              <Link to="/pacientes">
                <Button variant="ghost" size="sm" className="text-slate-600" data-testid="view-patients-btn">
                  Ver todos
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {stats?.high_risk_patients?.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay pacientes de alto riesgo</p>
              ) : (
                <div className="space-y-3">
                  {stats?.high_risk_patients?.map((patient, index) => {
                    const recordId = resolvePatientRecordId(patient);
                    return (
                    <PatientRecordLink
                      key={recordId || `high-risk-${index}`}
                      record={patient}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50/80 hover:shadow-md block"
                      testId={`high-risk-patient-${recordId || "unlinked"}`}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{patientRecordLabel(patient)}</p>
                        <p className="text-sm text-slate-500">{patient.diagnostico_principal || (patient.flags || []).join(" • ")}</p>
                      </div>
                      <RiskBadge level="alto" />
                    </PatientRecordLink>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {LATAM_SHOW_APPOINTMENT_CARDS && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-600" />
                Próximas Citas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {stats?.upcoming_appointments?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No hay citas próximas</p>
                ) : (
                  <div className="space-y-3">
                    {stats?.upcoming_appointments?.map((apt) => (
                      <div
                        key={apt.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-50"
                        data-testid={`upcoming-${apt.id}`}
                      >
                        <div>
                          <p className="font-medium text-slate-800">{apt.paciente_nombre}</p>
                          <p className="text-sm text-slate-500">{apt.motivo}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-800">
                            {new Date(apt.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                          </p>
                          <p className="text-sm text-slate-500">{apt.hora}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Patients Without Recent Visit */}
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
              <UserX className="h-5 w-5 shrink-0 text-slate-600" />
              Sin Consulta en +6 Meses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {stats?.patients_without_recent_visit?.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Todos los pacientes tienen consultas recientes</p>
              ) : (
                <div className="space-y-3">
                  {stats?.patients_without_recent_visit?.map((patient, index) => {
                    const recordId = resolvePatientRecordId(patient);
                    return (
                    <PatientRecordLink
                      key={recordId || `no-visit-${index}`}
                      record={patient}
                      className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50/80 hover:shadow-md block"
                      testId={`no-visit-patient-${recordId || "unlinked"}`}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{patientRecordLabel(patient)}</p>
                        <p className="text-sm text-slate-500">{patient.diagnostico_principal}</p>
                      </div>
                      <Button variant="outline" size="sm" className="border-slate-200 text-slate-700">
                        Agendar
                      </Button>
                    </PatientRecordLink>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Clinical Alerts */}
      {!isUsHost && alerts && (
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
          <CardHeader className="pb-3">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
                <Activity className="h-5 w-5 shrink-0 text-slate-600" />
                <span>Alertas Clínicas Avanzadas</span>
                {(safeAdvancedAlerts?.total_alertas_criticas ?? 0) > 0 && (
                  <Badge className="ml-0 border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800 sm:ml-2">
                    {safeAdvancedAlerts?.total_alertas_criticas ?? 0} críticas
                  </Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeAlertTab} onValueChange={setActiveAlertTab}>
              <TabsList className="grid w-full grid-cols-4 lg:grid-cols-9">
                <TabsTrigger value="caidas" className="text-xs">
                  <ShieldAlert className="h-3 w-3 mr-1" />
                  Caídas ({safeAlertTotals?.alto_riesgo_caidas ?? 0})
                </TabsTrigger>
                <TabsTrigger value="fragilidad" className="text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Fragilidad ({safeAlertTotals?.fragilidad_alta ?? 0})
                </TabsTrigger>
                <TabsTrigger value="polifarmacia" className="text-xs">
                  <Pill className="h-3 w-3 mr-1" />
                  Polifarmacia ({safeAlertTotals?.polifarmacia_severa ?? 0})
                </TabsTrigger>
                <TabsTrigger value="beers_hallazgos" className="text-xs">
                  <Pill className="mr-1 h-3 w-3 text-slate-500" />
                  BEERS ({safeAlertTotals?.beers_hallazgos ?? 0})
                </TabsTrigger>
                <TabsTrigger value="beers_pendiente" className="text-xs">
                  <Pill className="mr-1 h-3 w-3 text-slate-500" />
                  Revisión ({safeAlertTotals?.beers_pendiente ?? 0})
                </TabsTrigger>
                <TabsTrigger value="malnutricion" className="text-xs">
                  <Apple className="h-3 w-3 mr-1" />
                  Nutrición ({safeAlertTotals?.malnutricion ?? 0})
                </TabsTrigger>
                <TabsTrigger value="cognitivo" className="text-xs">
                  <Brain className="h-3 w-3 mr-1" />
                  Cognitivo ({safeAlertTotals?.deterioro_cognitivo ?? 0})
                </TabsTrigger>
                <TabsTrigger value="social" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  Social ({safeAlertTotals?.riesgo_social_alto ?? 0})
                </TabsTrigger>
                <TabsTrigger value="upp" className="text-xs">
                  <Heart className="h-3 w-3 mr-1" />
                  UPP ({safeAlertTotals?.riesgo_upp ?? 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="caidas" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.alto_riesgo_caidas) ? safeAlertBuckets.alto_riesgo_caidas : []} emptyMessage="Sin pacientes con alto riesgo de caídas" />
              </TabsContent>
              <TabsContent value="fragilidad" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.fragilidad_alta) ? safeAlertBuckets.fragilidad_alta : []} emptyMessage="Sin pacientes con fragilidad alta" />
              </TabsContent>
              <TabsContent value="polifarmacia" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.polifarmacia_severa) ? safeAlertBuckets.polifarmacia_severa : []} emptyMessage="Sin pacientes con polifarmacia severa" />
              </TabsContent>
              <TabsContent value="beers_hallazgos" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.beers_hallazgos) ? safeAlertBuckets.beers_hallazgos : []} emptyMessage="Sin pacientes con hallazgos BEERS" alertColor="red" />
              </TabsContent>
              <TabsContent value="beers_pendiente" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.beers_pendiente) ? safeAlertBuckets.beers_pendiente : []} emptyMessage="Sin pacientes con revisión BEERS pendiente" alertColor="amber" />
              </TabsContent>
              <TabsContent value="malnutricion" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.malnutricion) ? safeAlertBuckets.malnutricion : []} emptyMessage="Sin pacientes con malnutrición" />
              </TabsContent>
              <TabsContent value="cognitivo" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.deterioro_cognitivo) ? safeAlertBuckets.deterioro_cognitivo : []} emptyMessage="Sin pacientes con deterioro cognitivo severo" />
              </TabsContent>
              <TabsContent value="social" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.riesgo_social_alto) ? safeAlertBuckets.riesgo_social_alto : []} emptyMessage="Sin pacientes con riesgo social alto" />
              </TabsContent>
              <TabsContent value="upp" className="mt-4">
                <AlertList patients={Array.isArray(safeAlertBuckets?.riesgo_upp) ? safeAlertBuckets.riesgo_upp : []} emptyMessage="Sin pacientes con alto riesgo de UPP" />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <p className="pt-6 text-center text-xs text-slate-500">Diseñado para la práctica geriátrica en Latinoamérica.</p>
    </div>
  );
}

const AlertList = ({ patients, emptyMessage, alertColor = "red" }) => {
  if (!patients || patients.length === 0) {
    return <p className="text-slate-500 text-center py-6">{emptyMessage}</p>;
  }

  const bgColor = "bg-white hover:bg-slate-50/90";
  const badgeColor =
    alertColor === "amber"
      ? "border-sky-200 bg-sky-50 text-sky-800"
      : "border-slate-200 bg-slate-100 text-slate-800";

  return (
    <ScrollArea className="h-48">
      <div className="space-y-2">
        {patients.map((patient, index) => {
          const recordId = resolvePatientRecordId(patient);
          return (
          <PatientRecordLink
            key={recordId || `alert-${index}`}
            record={patient}
            className={`flex min-w-0 items-center justify-between rounded-2xl border border-slate-200 p-3 shadow-sm transition-all hover:shadow-md ${bgColor} block`}
          >
            <div className="min-w-0 pr-2">
              <p className="font-medium text-slate-800">{patientRecordLabel(patient)}</p>
              <p className="text-sm text-slate-500">{patient.edad} años</p>
            </div>
            <Badge className={`shrink-0 px-2.5 py-0.5 text-xs font-medium ${badgeColor}`}>
              {patient.alerta}
            </Badge>
          </PatientRecordLink>
          );
        })}
      </div>
    </ScrollArea>
  );
}
